import React, { useState } from 'react';
import {
  Search,
  BookOpen,
  Layers,
  Users,
  Calendar,
  AlertCircle,
  BarChart2,
  Globe,
  FileText,
  Settings,
  Sparkles,
  LayoutDashboard,
  Search as SearchIcon,
  Video,
  PlayCircle,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Columns,
  Bookmark,
  CheckCircle2,
  Clock,
  SlidersHorizontal,
  Check,
  Bell,
  Send,
  ArrowUpDown,
  ShieldCheck,
  Megaphone,
  Mail,
  Tag
} from 'lucide-react';

interface HelpSection {
  id: string;
  icon: React.ReactNode;
  title: string;
  category: 'video' | 'core' | 'tools' | 'settings' | 'whats-new';
  badge?: string;
  keywords: string[];
  content: React.ReactNode;
}

export default function HelpCenter() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'video' | 'core' | 'tools' | 'settings' | 'whats-new'>('all');
  const [showFullTranscript, setShowFullTranscript] = useState(false);

  const transcriptItems = [
    { time: '00:00', text: 'This tutorial guides you through configuring opportunity items and customizing views within the Armellini CRM workspace.' },
    { time: '00:07', text: 'You will learn how to manage columns, adjust settings, and save your customized view for efficient workflow.' },
    { time: '00:14', text: 'Click "Opportunity Items / Add to Opportunity" to open the opportunity items section within the Armellini CRM workspace.' },
    { time: '00:23', text: 'Click "Show/Hide Columns" to manage which columns are visible in your workspace view.' },
    { time: '00:30', text: 'Toggle the visibility of specific columns in the opportunity items list according to your preference.' },
    { time: '00:48', text: 'Select additional columns or remove unnecessary ones from your active table view.' },
    { time: '01:04', text: 'Review and adjust your column order and display settings as needed for optimal presentation.' },
    { time: '01:52', text: 'Customize the layout of columns for better freight and lead data management.' },
    { time: '02:11', text: 'Enable selected features or settings within the workspace configuration.' },
    { time: '02:47', text: 'Update your view settings and improve data presentation for your sales pipeline.' },
    { time: '03:54', text: 'Activate workspace features or settings to fit your workflow needs.' },
    { time: '04:43', text: 'Click "Save View" to save your customized column and workspace settings as a reusable Smart View for future use.' },
    { time: '04:49', text: 'Access and switch between your saved workspace views and configurations.' },
    { time: '05:21', text: 'Click "Next Steps / Updates" to view and manage upcoming actions and updates related to your opportunities.' },
    { time: '05:33', text: 'Click "Full Quote View" to see the complete quote details for your opportunity.' },
    { time: '05:49', text: 'You have successfully configured opportunity items and customized your workspace view in Armellini CRM.' }
  ];

  const sections: HelpSection[] = [
    {
      id: 'whats-new-v186',
      icon: <Sparkles className="w-6 h-6 text-indigo-500" />,
      title: "What's New in Version 1.8.6 (Release Notes & Changelog)",
      category: 'whats-new',
      badge: 'Release v1.8.6 — October 28, 2026',
      keywords: [
        'whats new', 'release notes', 'changelog', 'v1.8.6', '1.8.6', 'pipeline status',
        'milestone dates', 'edit lead details', 'approved lanes', 'rate agreement role',
        'bill to', 'service frequency', 'bi-weekly', 'read-only status', 'credit application',
        'sent to pricing', 'quoted date', 'activity history'
      ],
      content: (
        <div className="space-y-6">
          <div className="p-4 rounded-xl bg-gradient-to-r from-indigo-50 via-blue-50 to-slate-50 dark:from-indigo-950/40 dark:via-blue-950/30 dark:to-slate-900/50 border border-indigo-200/60 dark:border-indigo-900/40">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2.5 py-0.5 rounded-full text-xs font-black uppercase tracking-wider bg-indigo-600 text-white shadow-xs">
                PRODUCTION RELEASE v1.8.6
              </span>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                Updated October 28, 2026
              </span>
            </div>
            <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
              Armellini CRM Version 1.8.6 introduces a strictly milestone-driven Pipeline Status & Category engine with read-only status badges across the Opportunity Dashboard and Lead Drawer, an in-drawer Edit CRM Lead Details modal, full edit/remove support for Approved Lanes with Activity History audit logging, and updated Rate Agreement Roles & Service Frequencies.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-slate-100 text-sm">
                    <SlidersHorizontal className="w-4 h-4 text-indigo-500" />
                    <span>1. Milestone-Driven Pipeline Status & Read-Only Badges</span>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-400 border border-blue-300 dark:border-blue-800">
                    IMPROVED
                  </span>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed mb-3">
                  Pipeline Status and Category are now 100% automatically calculated from milestone dates entered in the Dates tab and Lane Quoting.
                </p>
                <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1.5 list-disc list-inside">
                  <li><strong>Read-Only Status Badges:</strong> Removed manual dropdown carets from Status badges on the Opportunity Dashboard (List View) and added a read-only Status & Category banner inside the Lead Drawer.</li>
                  <li><strong>Sent to Pricing & Credit App Logic:</strong> Entering a Sent to Pricing Date transitions leads to <code>Quoted</code>. Credit Application submissions log <em>"Credit Application received"</em> to Activity History and Quick Notes and auto-check Credit App Sent without overriding Status.</li>
                </ul>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-slate-100 text-sm">
                    <Settings className="w-4 h-4 text-emerald-500" />
                    <span>2. Edit CRM Lead Details Modal</span>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800">
                    NEW
                  </span>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed mb-3">
                  Update core customer and contact details directly from the Lead Slide-Over Drawer header via the new <strong>Edit Details</strong> button.
                </p>
                <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1.5 list-disc list-inside">
                  <li><strong>Editable Core Fields:</strong> Company Name, Lead Type (Customer / Shipper), Commodity Type, Primary Contact, Phone, Email, Address, and Current Freight Solution.</li>
                  <li><strong>Automatic Change Auditing:</strong> Every modified field is recorded in Activity History with its previous and new values.</li>
                </ul>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-slate-100 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-teal-500" />
                    <span>3. Editable Approved Lanes & Synced Quoting Dates</span>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-teal-100 dark:bg-teal-950/60 text-teal-700 dark:text-teal-400 border border-teal-300 dark:border-teal-800">
                    IMPROVED
                  </span>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed mb-3">
                  Approved lanes can now be edited or removed with full audit visibility, while quoting dates stay locked to lane actions.
                </p>
                <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1.5 list-disc list-inside">
                  <li><strong>Approved Lane Audit Trail:</strong> Editing or removing an approved lane logs the changed field, old and new values, date, and user to Activity History.</li>
                  <li><strong>Read-Only Quoting Dates:</strong> Quoted Date and Sent to Pricing Date are read-only in the Dates tab and automatically populate when lanes are quoted or sent to pricing.</li>
                </ul>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-slate-100 text-sm">
                    <FileText className="w-4 h-4 text-purple-500" />
                    <span>4. Rate Agreement Role & Service Frequency Updates</span>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 dark:bg-purple-950/60 text-purple-700 dark:text-purple-400 border border-purple-300 dark:border-purple-800">
                    NEW
                  </span>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed mb-3">
                  Expanded billing role flexibility and refined lane quoting frequency options.
                </p>
                <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1.5 list-disc list-inside">
                  <li><strong>Rate Agreement Role:</strong> Added standalone <code>Bill To</code> alongside <code>Bill To/Consignee</code> and <code>Bill To/Shipper</code> across Lane Quoting and Client Forms.</li>
                  <li><strong>Service Frequency:</strong> Removed <code>Bi-Weekly</code> from the Service Frequency dropdown options.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'whats-new-v184',
      icon: <Sparkles className="w-6 h-6 text-indigo-500" />,
      title: "What's New in Version 1.8.4 (Release Notes & Changelog)",
      category: 'whats-new',
      badge: 'Release v1.8.4 — October 26, 2026',
      keywords: [
        'whats new', 'release notes', 'changelog', 'v1.8.4', '1.8.4', 'rate agreement',
        'bill to consignee', 'bill to shipper', 'payment terms', 'client forms',
        'authedFetch', 'docuseal', 'pci dss', 'credit card masking', 'webhook', 'cdata cache',
        'gemini 4 argon', 'ai assistant', 'resizable chat', 'signed pdf download'
      ],
      content: (
        <div className="space-y-6">
          <div className="p-4 rounded-xl bg-gradient-to-r from-indigo-50 via-blue-50 to-slate-50 dark:from-indigo-950/40 dark:via-blue-950/30 dark:to-slate-900/50 border border-indigo-200/60 dark:border-indigo-900/40">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2.5 py-0.5 rounded-full text-xs font-black uppercase tracking-wider bg-indigo-600 text-white shadow-xs">
                PRODUCTION RELEASE v1.8.4
              </span>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                Updated October 26, 2026
              </span>
            </div>
            <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
              Armellini CRM Version 1.8.4 delivers full 17-field Rate Agreement template alignment, auto-population of quoting and delivery fields directly from CRM Lead records, Bill To/Consignee vs. Bill To/Shipper role support, official top-left AEL branding, and live document activity history tracking.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-slate-100 text-sm">
                    <FileText className="w-4 h-4 text-indigo-500" />
                    <span>1. Authenticated Form Invites & Signed PDF Downloads</span>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800">
                    NEW
                  </span>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed mb-3">
                  Client Form invitations and document downloads in the Lead Slide-Over Drawer now use Bearer-authenticated requests end to end.
                </p>
                <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1.5 list-disc list-inside">
                  <li><strong>Form Invitations:</strong> Create, resend, revoke, and sync DocuSeal status with full Firebase token authentication.</li>
                  <li><strong>Protected PDF Downloads:</strong> Download signed agreements and lead attachments directly via authenticated blob streams.</li>
                </ul>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-slate-100 text-sm">
                    <ShieldCheck className="w-4 h-4 text-teal-500" />
                    <span>2. PCI DSS Card Sanitization & Webhook Security</span>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-teal-100 dark:bg-teal-950/60 text-teal-700 dark:text-teal-400 border border-teal-300 dark:border-teal-800">
                    SECURITY
                  </span>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed mb-3">
                  Hardened data protection across public form submissions, e-signature webhooks, email templates, and graph memory queries.
                </p>
                <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1.5 list-disc list-inside">
                  <li><strong>Card Masking:</strong> Automatically strips raw CVV codes and masks card numbers to <code>•••• •••• •••• 1234</code> before database storage.</li>
                  <li><strong>Verified Webhooks:</strong> Validates <code>DOCUSEAL_WEBHOOK_SECRET</code> and verifies completion status against the DocuSeal API.</li>
                </ul>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-slate-100 text-sm">
                    <SlidersHorizontal className="w-4 h-4 text-blue-500" />
                    <span>3. Faster Lead Operations & CData View Caching</span>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-400 border border-blue-300 dark:border-blue-800">
                    PERFORMANCE
                  </span>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed mb-3">
                  Eliminated full-table scans during single-lead updates and added intelligent caching for external data connectors.
                </p>
                <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1.5 list-disc list-inside">
                  <li><strong>Indexed Lookups:</strong> Single-lead duplicate, archive, restore, and form actions use <code>getLeadById()</code> and atomic JSONB activity appends.</li>
                  <li><strong>60s CData Cache & Batched Sync:</strong> Cached upstream CData views and batched Non-LTL Google Sheet imports.</li>
                </ul>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-slate-100 text-sm">
                    <Sparkles className="w-4 h-4 text-purple-500" />
                    <span>4. Gemini 4 Argon AI Assistant & Resizable Workspace</span>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 dark:bg-purple-950/60 text-purple-700 dark:text-purple-400 border border-purple-300 dark:border-purple-800">
                    AI UPGRADE
                  </span>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed mb-3">
                  Upgraded the CRM AI Assistant to Gemini 4 Argon and Gemini 3.8 Flash on Halo AI with a customizable 900px workspace.
                </p>
                <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1.5 list-disc list-inside">
                  <li><strong>Resizable 900px Window:</strong> Drag any edge or corner to resize, or click Expand to toggle between 900px and full-screen mode.</li>
                  <li><strong>1-Click Table Exports:</strong> Export AI-generated tables and reports directly to PDF, CSV, or Markdown.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'whats-new-v174',
      icon: <Sparkles className="w-6 h-6 text-indigo-500" />,
      title: "What's New in Version 1.7.4 (Release Notes & Changelog)",
      category: 'whats-new',
      badge: 'Release v1.7.4 — September 28, 2026',
      keywords: [
        'whats new', 'release notes', 'changelog', 'v1.7.4', '1.7.4', 'notifications',
        'click notifications', 'open record', 'deep link', 'online form', 'embedded form',
        'contract of carriage', 'opportunity lead', 'yellow alert', 'due today', 'sorting'
      ],
      content: (
        <div className="space-y-6">
          <div className="p-4 rounded-xl bg-gradient-to-r from-indigo-50 via-blue-50 to-slate-50 dark:from-indigo-950/40 dark:via-blue-950/30 dark:to-slate-900/50 border border-indigo-200/60 dark:border-indigo-900/40">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2.5 py-0.5 rounded-full text-xs font-black uppercase tracking-wider bg-indigo-600 text-white shadow-xs">
                PRODUCTION RELEASE v1.7.4
              </span>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                Deployed September 28, 2026
              </span>
            </div>
            <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
              Armellini CRM Version 1.7.4 introduces interactive notification record navigation, online lead form submission alerts with direct CRM links, Contract of Carriage legal gatekeeping, and automated sorting for Due-Today Yellow Alerts and Opportunity &gt;&gt; Lead records.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 1. Clickable Notifications */}
            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-slate-100 text-sm">
                    <Bell className="w-4 h-4 text-indigo-500" />
                    <span>1. Click Notifications to Open Records</span>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800">
                    NEW FEATURE
                  </span>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed mb-3">
                  Clicking any notification in the top header bell or clicking a system desktop push alert now directly opens the associated lead in the Slide-Over drawer.
                </p>
                <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1.5 list-disc list-inside">
                  <li><strong>Single-Lead Notices:</strong> Auto-navigates and opens drawer with full record details.</li>
                  <li><strong>Multi-Lead Groups (e.g. "4 follow-ups due"):</strong> Renders company chips allowing direct jump to any individual lead.</li>
                  <li><strong>Heuristic Resolver:</strong> Intelligently matches company names and contact aliases from unlinked notices.</li>
                </ul>
              </div>
            </div>

            {/* 2. Online Lead Form Notifications */}
            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-slate-100 text-sm">
                    <Send className="w-4 h-4 text-emerald-500" />
                    <span>2. Online Lead Form Alerts & Dispatch</span>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-400 border border-blue-300 dark:border-blue-800">
                    LEAD INTAKE
                  </span>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed mb-3">
                  Freight quote submissions from the online lead form (direct link or embedded website iframe) trigger automated notifications and email workflows.
                </p>
                <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1.5 list-disc list-inside">
                  <li><strong>Sales Alert:</strong> Dispatches real-time email to <code>sales@armellini.com</code> with direct CRM deep link button.</li>
                  <li><strong>Customer Confirmation:</strong> Sends branded quote receipt confirmation to the submitter.</li>
                  <li><strong>Source Tagging:</strong> Distinguishes between direct link and embedded iframe submissions with audit logging.</li>
                </ul>
              </div>
            </div>

            {/* 3. Opportunity >> Lead Status Priority */}
            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-slate-100 text-sm">
                    <ArrowUpDown className="w-4 h-4 text-amber-500" />
                    <span>3. Opportunity &gt;&gt; Lead & Yellow Alert Priority</span>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-800">
                    PIPELINE
                  </span>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed mb-3">
                  Default pipeline ordering prioritizes newly entered opportunities and immediate follow-ups so reps never miss incoming customer inquiries.
                </p>
                <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1.5 list-disc list-inside">
                  <li><strong>Top Pinning:</strong> Records in <code>Opportunity &gt;&gt; Lead</code> status are pinned at the top of the Status column view.</li>
                  <li><strong>Default Follow-up Date:</strong> Automatically assigned to the Date Entered (inquiry timestamp).</li>
                  <li><strong>Yellow Alerts (Due Today):</strong> Given priority ranking in default workspace views for immediate outreach.</li>
                </ul>
              </div>
            </div>

            {/* 4. Contract of Carriage Legal Acceptance */}
            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-slate-100 text-sm">
                    <ShieldCheck className="w-4 h-4 text-blue-500" />
                    <span>4. Contract of Carriage Legal Acceptance</span>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 dark:bg-purple-950/60 text-purple-700 dark:text-purple-400 border border-purple-300 dark:border-purple-800">
                    LEGAL
                  </span>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed mb-3">
                  Full legal protection for Armellini freight operations integrated directly into the customer rate agreement onboarding workflow.
                </p>
                <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1.5 list-disc list-inside">
                  <li><strong>Mandatory Verification:</strong> Rate Agreement signers must view and accept the official Armellini Contract of Carriage before signing.</li>
                  <li><strong>PDF Preview & Download:</strong> Signers can view the complete Contract of Carriage PDF document or download for records.</li>
                  <li><strong>Audit Trail:</strong> Consent timestamp and client IP are cryptographically recorded in the agreement audit trail.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'video-custom-views',
      icon: <Video className="w-6 h-6 text-rose-500" />,
      title: 'Video Tutorial: Configuring & Customizing Views in Sales CRM',
      category: 'video',
      badge: 'Interactive Video Guide (6:00 min)',
      keywords: [
        'video', 'guidde', 'custom view', 'customizing views', 'columns', 'show hide columns',
        'reorder columns', 'save view', 'smart view', 'opportunity items', 'full quote view',
        'next steps', 'updates', 'tutorial', 'walkthrough'
      ],
      content: (
        <div className="space-y-6">
          <p className="text-slate-600 dark:text-slate-300">
            Learn how to tailor your Armellini CRM workspace by configuring opportunity items, managing table column visibility, enabling workspace features, and saving personalized Smart Views.
          </p>

          {/* Embedded Video Container */}
          <div className="w-full bg-slate-950 rounded-2xl p-2 sm:p-4 shadow-xl border border-slate-200 dark:border-slate-800">
            <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-black shadow-inner">
              <iframe
                className="w-full h-full"
                src="https://embed.app.guidde.com/playbooks/wud6DJNWdUG4VSz33DENUF?mode=videoOnly"
                title="How to Configure Customize Views in Sales CRM"
                frameBorder="0"
                referrerPolicy="unsafe-url"
                allowFullScreen
                allow="clipboard-write"
                sandbox="allow-popups allow-popups-to-escape-sandbox allow-scripts allow-forms allow-same-origin allow-presentation"
                style={{ borderRadius: '10px' }}
              />
            </div>

            {/* Video Controls / Info Bar */}
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3 px-2 text-xs text-slate-400">
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1.5 font-medium text-slate-300">
                  <PlayCircle className="w-4 h-4 text-rose-500" /> Guidde Interactive Playbook
                </span>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" /> 6:00 min
                </span>
              </div>
              <a
                href="https://embed.app.guidde.com/playbooks/wud6DJNWdUG4VSz33DENUF?mode=videoOnly"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 font-semibold hover:underline"
              >
                Watch on Guidde <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>

          {/* Key Topics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60">
              <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-slate-100 mb-1.5 text-sm">
                <Columns className="w-4 h-4 text-blue-500" />
                <span>1. Show / Hide & Reorder Columns</span>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                Click <strong>"Show/Hide Columns"</strong> in the dashboard toolbar to toggle field visibility (such as Estimated Value, Next Steps, Consignee/Shipper Account) and arrange them in your preferred sequence.
              </p>
            </div>

            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60">
              <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-slate-100 mb-1.5 text-sm">
                <SlidersHorizontal className="w-4 h-4 text-emerald-500" />
                <span>2. Feature Activation & Settings</span>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                Enable specific workspace settings and configuration options to adjust how opportunity items and data views are rendered across your team.
              </p>
            </div>

            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60">
              <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-slate-100 mb-1.5 text-sm">
                <Bookmark className="w-4 h-4 text-purple-500" />
                <span>3. Saving Custom Smart Views</span>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                Click <strong>"Save View"</strong> to preserve your custom columns, filters, and sorting criteria under a dedicated view name for instant one-click switching.
              </p>
            </div>

            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60">
              <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-slate-100 mb-1.5 text-sm">
                <CheckCircle2 className="w-4 h-4 text-amber-500" />
                <span>4. Next Steps & Full Quote Details</span>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                Quickly access <strong>"Next Steps / Updates"</strong> to track upcoming actions, or open the <strong>"Full Quote View"</strong> to inspect comprehensive rate matrix breakdowns.
              </p>
            </div>
          </div>

          {/* Interactive Transcript Section */}
          <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
            <button
              onClick={() => setShowFullTranscript(!showFullTranscript)}
              className="w-full flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/70 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer text-left"
            >
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-slate-500" />
                <span className="font-bold text-sm text-slate-800 dark:text-slate-200">
                  Step-by-Step Video Transcript & Timestamps
                </span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300 font-semibold">
                  {transcriptItems.length} Milestones
                </span>
              </div>
              {showFullTranscript ? (
                <ChevronUp className="w-4 h-4 text-slate-500" />
              ) : (
                <ChevronDown className="w-4 h-4 text-slate-500" />
              )}
            </button>

            {showFullTranscript && (
              <div className="p-4 bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800/80 max-h-80 overflow-y-auto space-y-2">
                {transcriptItems.map((item, idx) => (
                  <div key={idx} className="pt-2.5 first:pt-0 flex items-start gap-3">
                    <span className="inline-block px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 font-mono text-xs font-semibold shrink-0">
                      {item.time}
                    </span>
                    <span className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-normal">
                      {item.text}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      id: 'dashboard',
      icon: <LayoutDashboard className="w-6 h-6 text-blue-500" />,
      title: 'Dashboard & Kanban Board',
      category: 'core',
      keywords: ['dashboard', 'kanban', 'board', 'pipeline', 'drag and drop', 'stages', 'table view', 'filter', 'sort'],
      content: (
        <div className="space-y-4">
          <p>
            The <strong>Dashboard</strong> is your central hub for managing leads and quotes across their lifecycle. The Pipeline view uses a Kanban-style layout where each column represents a different stage in your sales process.
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Drag and Drop:</strong> You can drag and drop leads between columns to update their status instantly.</li>
            <li><strong>Filtering & Sorting:</strong> Use the search bar at the top to quickly find specific leads or filter by agent and value.</li>
            <li><strong>Board vs Table View:</strong> Toggle between the visual Kanban Board and a compact Table View using the icons in the upper right.</li>
            <li><strong>Quick Actions:</strong> Click on any lead card to open the Slide-over details panel.</li>
          </ul>
        </div>
      )
    },
    {
      id: 'directory-search',
      icon: <SearchIcon className="w-6 h-6 text-purple-500" />,
      title: 'Consignee & Shipper Directory',
      category: 'core',
      keywords: ['directory', 'consignee', 'shipper', 'cdata', 'search', 'get quote', 'account number', 'contacts'],
      content: (
        <div className="space-y-4">
          <p>
            The <strong>Consignee & Shipper Directory</strong> is a master database of all active Armellini shipping accounts.
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Comprehensive Search:</strong> Type any part of an account name, number, or contact into the search bar. The system will filter the extensive list down to matching results.</li>
            <li><strong>Get a Quote:</strong> Click "Get a Quote" next to any directory result. This instantly bridges the account into your Active CRM Leads pipeline.</li>
            <li><strong>Automatic Sync:</strong> Quoting an account will automatically populate the Lead Details (Account Number, Company Name, and Default Contacts) into your slide-over view.</li>
          </ul>
        </div>
      )
    },
    {
      id: 'lead-details',
      icon: <Users className="w-6 h-6 text-indigo-500" />,
      title: 'Lead Details & Quotes',
      category: 'core',
      keywords: ['lead details', 'quote', 'slide-over', 'activity log', 'notes', 'contacts', 'deal value', 'audit'],
      content: (
        <div className="space-y-4">
          <p>
            When you select a lead from the Dashboard or choose to create a new quote from the Consignee Search, the <strong>Slide-over panel</strong> opens on the right side of your screen.
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Primary Information:</strong> Update company name, contact info, pipeline category, and deal values.</li>
            <li><strong>Activity Log & Notes:</strong> Add notes to a lead to keep a chronological history. A timestamped history of all changes and notes added to the lead is stored automatically.</li>
            <li><strong>Save & Update:</strong> Any changes made here are saved automatically, recording your username and the exact time of the update.</li>
          </ul>
        </div>
      )
    },
    {
      id: 'follow-ups',
      icon: <Calendar className="w-6 h-6 text-green-500" />,
      title: 'Follow-ups & Google Workspace',
      category: 'tools',
      keywords: ['google', 'calendar', 'workspace', 'follow up', 'reminder', 'events', 'sync', 'google hub'],
      content: (
        <div className="space-y-4">
          <p>
            Never miss a follow-up with our seamless Google Workspace and Calendar integration.
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Setting a Date:</strong> In the Lead Details panel, pick a <strong>Follow-up Date</strong>.</li>
            <li><strong>Automatic Calendar Sync:</strong> When you hit Save, the CRM automatically schedules a follow-up event on your Google Calendar for <strong>8:00 AM</strong> on the selected day.</li>
            <li><strong>Reminders & Availability:</strong> The event is marked as "Free" so it doesn't block your schedule, but it includes automatic pop-up and email reminders to ensure you're notified.</li>
            <li><strong>Google Hub:</strong> Visit the Google Workspace tab to sign in and grant calendar permissions if it's your first time using the sync feature.</li>
          </ul>
        </div>
      )
    },
    {
      id: 'analytics',
      icon: <BarChart2 className="w-6 h-6 text-orange-500" />,
      title: 'Analytics & KPI Tracking',
      category: 'tools',
      keywords: ['analytics', 'kpi', 'reports', 'charts', 'conversion', 'win rate', 'pipeline value', 'metrics'],
      content: (
        <div className="space-y-4">
          <p>
            The <strong>Analytics</strong> module provides top-level insights into your sales performance and pipeline health.
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>KPI Cards:</strong> Track Total Active Leads, Win Rates, Pipeline Value, and Recent Conversion Metrics.</li>
            <li><strong>Visual Charts:</strong> Review historical trends and breakdown of leads by stage or by salesperson.</li>
            <li><strong>Date Ranges:</strong> Filter your reporting by specific weeks, months, or quarters to generate accurate commission or progress reports.</li>
          </ul>
        </div>
      )
    },
    {
      id: 'client-forms',
      icon: <FileText className="w-6 h-6 text-teal-500" />,
      title: 'Client Forms & Questionnaires',
      category: 'tools',
      keywords: ['forms', 'questionnaires', 'lost customer', 'new account', 'intake', 'digital submission'],
      content: (
        <div className="space-y-4">
          <p>
            The <strong>Client Forms</strong> section digitizes the standard intake and feedback questionnaires.
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Digital Submission:</strong> Fill out forms like the "Lost Customer Questionnaire" or "New Account Setup" directly in the app.</li>
            <li><strong>Validation:</strong> Required fields ensure that no critical information is skipped before a form is submitted to management.</li>
            <li><strong>History:</strong> Past forms submitted for an account can be retrieved from the central forms repository.</li>
          </ul>
        </div>
      )
    },
    {
      id: 'non-floral',
      icon: <Layers className="w-6 h-6 text-rose-500" />,
      title: 'Non-Floral LTL Module',
      category: 'core',
      keywords: ['non-floral', 'ltl', 'cargo', 'billing history', 'lara', 'email queue', 'audit', 'freight'],
      content: (
        <div className="space-y-4">
          <p>
            The <strong>Non-Floral LTL</strong> workspace is a dedicated environment for managing non-floral freight and special account configurations.
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Data Entry & Active LTL:</strong> Manage active shipments that fall outside of the standard floral routing.</li>
            <li><strong>Billing History:</strong> View historical billing and invoicing data specific to non-floral accounts.</li>
            <li><strong>Lara Email Queue:</strong> Monitor and manage the automated Lara email communications queue.</li>
            <li><strong>Account Maintenance & Audit:</strong> Adjust configurations and track a strict audit log of all changes made to non-floral profiles.</li>
          </ul>
        </div>
      )
    },
    {
      id: 'ai-advisor',
      icon: <Sparkles className="w-6 h-6 text-purple-650" />,
      title: 'AI CRM Advisor',
      category: 'tools',
      keywords: ['ai', 'advisor', 'sparkles', 'gemini', 'summary', 'draft email', 'insights'],
      content: (
        <div className="space-y-4">
          <p>
            Your CRM includes a built-in <strong>AI Advisor</strong> powered by generative AI to help you work faster and smarter.
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Access:</strong> Click the Sparkles ✨ icon in the top right navigation bar to open the AI Advisor modal at any time.</li>
            <li><strong>Capabilities:</strong> Ask the AI to summarize a lead's activity, draft a follow-up email based on notes, or generate insights about your pipeline velocity.</li>
            <li><strong>Context Aware:</strong> The AI is aware of the current leads and stages you are viewing, allowing for highly relevant answers.</li>
          </ul>
        </div>
      )
    },
    {
      id: 'ui-themes',
      icon: <Settings className="w-6 h-6 text-slate-500" />,
      title: 'Themes & User Interface',
      category: 'settings',
      keywords: ['theme', 'dark mode', 'light mode', 'armellini blue', 'armellini dark', 'color', 'ui', 'sidebar'],
      content: (
        <div className="space-y-4">
          <p>
            Customize the look and feel of your CRM to match your personal preference or working environment.
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Color Modes:</strong> Click the Palette 🎨 icon in the top right corner to switch themes.</li>
            <li><strong>Available Themes:</strong> Choose between Light Mode, Original Dark Mode, Armellini Blue (dark blue accents), and Armellini Dark (high contrast dark mode).</li>
            <li><strong>Persistent Choice:</strong> Your theme selection is saved to your browser and will be remembered the next time you log in.</li>
            <li><strong>Responsive Design:</strong> The sidebar navigation can be collapsed on smaller screens or tablets by clicking the menu icon in the top left.</li>
          </ul>
        </div>
      )
    }
  ];

  const filteredSections = sections.filter((section) => {
    const q = searchQuery.toLowerCase().trim();
    const categoryMatches = selectedCategory === 'all' || section.category === selectedCategory;
    if (!categoryMatches) return false;
    if (!q) return true;

    const titleMatches = section.title.toLowerCase().includes(q);
    const idMatches = section.id.toLowerCase().includes(q);
    const keywordMatches = section.keywords.some((k) => k.toLowerCase().includes(q));
    return titleMatches || idMatches || keywordMatches;
  });

  return (
    <div className="flex-1 p-6 lg:p-10 max-w-5xl mx-auto w-full text-slate-800 dark:text-slate-200">
      {/* Header Banner */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            Comprehensive Help Center & Guides
          </h1>
          <p className="text-slate-500 mt-2">
            Browse documentation, watch interactive video playbooks, and learn how to customize the AEL CRM effectively.
          </p>
        </div>
        <div className="relative w-full md:w-80">
          <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search docs, videos, columns..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
          />
        </div>
      </div>

      {/* Category Filter Pills */}
      <div className="flex flex-wrap items-center gap-2 mb-8 border-b border-slate-200 dark:border-slate-800 pb-4">
        {[
          { id: 'all', label: 'All Topics' },
          { id: 'whats-new', label: '🚀 What’s New (v1.8.6)' },
          { id: 'video', label: '🎥 Video Walkthroughs' },
          { id: 'core', label: 'Pipeline & Directory' },
          { id: 'tools', label: 'Tools & Integrations' },
          { id: 'settings', label: 'Settings & UI' },
        ].map((cat) => (
          <button
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id as any)}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
              selectedCategory === cat.id
                ? 'bg-blue-600 text-white shadow-xs'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Topics Content */}
      {filteredSections.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
          <AlertCircle className="w-12 h-12 mb-4 opacity-50" />
          <p className="text-lg">No help topics found for "{searchQuery}"</p>
          <button
            onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}
            className="mt-4 px-4 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition"
          >
            Clear Filters
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {filteredSections.map((section) => (
            <section
              key={section.id}
              id={section.id}
              className="bg-white dark:bg-slate-900 p-6 sm:p-8 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-xl">
                    {section.icon}
                  </div>
                  <div>
                    <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 dark:text-slate-100">
                      {section.title}
                    </h2>
                  </div>
                </div>
                {section.badge && (
                  <span className="inline-flex items-center gap-1.5 self-start sm:self-auto px-3 py-1 rounded-full text-xs font-bold bg-rose-50 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-900/60">
                    <Video className="w-3.5 h-3.5" />
                    {section.badge}
                  </span>
                )}
              </div>
              <div className="text-slate-600 dark:text-slate-300 leading-relaxed text-base">
                {section.content}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
